import { IColumnScope } from "@app/components/kb-column/kb-column.component";
import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IEnumFilter } from "@app/filters/enum.filter";
import { Dirs } from "@app/helpers/dirs";
import { ApiService, CrudClient, ProductClient } from "@app/services/api.service";
import { DrawerService } from "@app/services/drawer.service";
import { SearchService } from "@app/services/search.service";
import { BaseController, IBaseScope } from "@app/views/base.view";
import { DialogButton, eAttributeType, eFilterControl, eFilterSource, eFilterType, eProductSortBy, IFilter, IKbRole, IMetaProperty, ISavedSearch, ISearchArgs, meta } from "@models";
import { icons } from "@tools";
import { TransitionOptions } from "@uirouter/angularjs";
import { IPromiseTracker } from "angular";
import { IProductsScope } from "./products/products.view";

export interface ISearchBinding{
    selected?: ISavedSearch;
    search?: ISavedSearch;
}

export interface ISearchScope<T> extends IBaseScope {
    allowSearch: boolean;
    availableFilters: IFilter[];
    results: T[];
    query: (append?: boolean) => ng.IPromise<T[]>;
    suggest: (q: string) => ng.IPromise<T[]>;
    suggestValueField: string;
    columns: IColumnScope[]; // gets filled by the view... useful for picking the fields we want returned in the query
    filterSource: {};
    addFilter: (f: IFilter) => void;
    removeFilter: (f: IFilter) => void;
    infiniteScroll: () => void;
    reload: (force?: boolean) => void;
    sort: (col: IColumnScope) => void;
    toggleFilters: () => void;
    modelType: string;
    modelTypes: { label: string; state: string; type: string }[];
    modelTypeChange: (modelType: { label: string; state: string; type: string }) => void;
    savedSearches: ISavedSearch[];
    editSearch: (search?: ISavedSearch) => void;
    deleteSearch: (s: ISavedSearch) => ng.IPromise<any>;
    canDeleteSearch: (s: ISavedSearch) => boolean;
    binding: ISearchBinding;
    drawerService: DrawerService;
    savedSearchEnabled: boolean;
    /** if you don't want a grid for the results, then set this to true and use the slot kb-search-grid-custom-results instead of kb-search-grid-results */
    customResults: boolean; 
}

export interface ISaveSearchDialogScope extends ng.IScope {
    roles?: IKbRole[];
    validation?: {
        name?: string;
    };
    model?: {
        name?: string;
        roles?: IKbRole[];
    };
}

/**
 * base class for handling search pages.     
 */
@NgView({
    token: Token.SearchView,
    dependencies: [
        Token.$Scope,
        Token.SearchService
    ]
})
export class SearchController<T> extends BaseController {
    constructor(
        protected $scope: ISearchScope<T>,
        public agg: SearchService,
        public bypassInit: boolean = false //for derived classes (like products view) that need to explicitly call init to have a chance to fill in other scope items first
    ) {

        super();

        agg.$rootScope.setTitle();

        $scope.drawerService = agg.drawerService;
        $scope.binding = { selected: null, search: null };
        $scope.results = [];
        $scope.savedSearchEnabled = false; //disabled by default.  Derived controllers can override
        $scope.allowSearch = true;

        $scope.query = (append: boolean = false) => {
            let filters: IFilter[] = []
                .concat($scope.binding.search.enabled ? $scope.binding.search.filters : [])
                .concat(this.getExtraFilters());
            if(append && this.epoch){
                //if we are appending to the infinite scroll, use the epoch as a filter so that we don't get duplicates
                filters.push(
                    {
                        property: "createdDate",
                        control: eFilterControl.dateRange,
                        type: eFilterType.property,
                        max: this.epoch
                    } as IFilter
                );
            } 
            if(!this.epoch){
                this.epoch = new Date();
            }
            this.postProcessFilters(filters);
            let sortInfo = this.getSortInfo();

            let args: ISearchArgs = {
                skip: append ? $scope.results.length : 0,
                take: this.pageSize,
                fields: this.fields(),
                query: $scope.binding.search.query,
                sortField: sortInfo.sortField,
                descending: sortInfo.descending,
                filters,
                nestedPath: this.getNestedPath(),
                nestedFields: this.nestedFields()
            };
            let tracker = append ? agg.$rootScope.infiniteScrollTracker : agg.$rootScope.contentTracker;

            return this.searchCall(args, tracker).then(r => {
                this.massageResults(r);
                append ? $scope.results.pushArray(r) : $scope.results = r;
                return r;
            });
        };

        $scope.suggestValueField = "name";
        $scope.suggest =  query => {
            if (query) {
                return this.client(agg.api).suggest({query, fields: [$scope.suggestValueField]});
            }
        };
        
        $scope.addFilter = f => {
            let newFilter = JSON.parse(JSON.stringify(f));
            $scope.binding.search.filters.push(newFilter);
            this.fillFilters();
        };
        $scope.removeFilter = f => {
            $scope.binding.search.filters.remove(f);
            // $scope.availableFilters.push(f);
            f.values = (f.control == eFilterControl.multiSelect? []: [null]);
            $scope.reload();
        };
        $scope.toggleFilters = () => {
            $scope.binding.search.enabled = !$scope.binding.search.enabled;
            $scope.reload();
        };
        $scope.sort = sortColumn => {
            if (sortColumn.sortable) {
                $scope.columns.forEach(col => {
                    if (col.field != sortColumn.field) {
                        col.sorted = false;
                        col.descending = false;
                    } else {
                        if (!col.sorted) {
                            col.sorted = true;
                            col.descending = false;
                        } else {
                            if (col.descending) {
                                col.sorted = false;
                                col.descending = false;
                            } else {
                                col.sorted = true;
                                col.descending = true;
                            }
                        }

                        if (col.sorted) {
                            $scope.binding.search.sortField = col.field;
                            $scope.binding.search.descending = col.descending;
                        } else {
                            $scope.binding.search.sortField = null;
                            $scope.binding.search.descending = false;
                        }
                    }
                });

                lastLength = 0;
                lastRetrieved = 0;

                $scope.reload();
            }
        };
        $scope.modelTypeChange = (modelType) => {
            agg.$state.go(modelType.state);
        };

        let lastLength = 0;
        let lastRetrieved = -1;
        $scope.infiniteScroll = () => {
            // if the list doesn't have at least the page size of items, it means there's no more
            // available, so no need to do another page
            let count = $scope.results.length;
            lastRetrieved = count == 0 ? -1 : count - lastLength;
            let noMore = count > 0 && (count % this.pageSize != 0);
            if (!noMore && (lastRetrieved < 0 || lastRetrieved == this.pageSize)) {
                lastLength = count;
                return $scope.query(true);
            }
        };

        $scope.reload = (force = false) => {
            let search = this.$scope.binding.search;
            
            let params = this.getQueryStringParams() || {};
            if (search.enabled){
                params = Object.assign(params, { search: agg.base64Service.encode(JSON.stringify(search)) });
            } else {
                //create a search without filters
                let basicSearch = {
                    ...search,
                    filters: []
                } as ISavedSearch;
                params = Object.assign(params, { search: agg.base64Service.encode(JSON.stringify(basicSearch)) });
            }

            let options: TransitionOptions = {};
            if (force) options.reload = true;
            this.epoch = null;
            agg.$state.go(agg.$state.current.name, params, options);
        };

        // set all filter sources to an empty array so we have a reference to bind to
        $scope.filterSource = {};
        for (let p in eFilterSource) $scope.filterSource[p] = [];

        this.filterSourceInfo = {};
        for (let e in eFilterSource) this.filterSourceInfo[e] = { valueField: "id", labelField: "name" };
        this.filterSourceInfo[eFilterSource.states] = { valueField: "value", labelField: "label" };
        this.filterSourceInfo[eFilterSource.roles] = { valueField: "id", labelField: "role" };
        this.filterSourceInfo[eFilterSource.manufacturers] = { valueField: null, labelField: null };
        this.filterSourceInfo[eFilterSource.enum] = {valueField: "value", labelField: "name"};

        $scope.editSearch = search => {
            this.saveSearchDialog(search);
        };

        $scope.deleteSearch = s => {
            return agg.api.savedSearches.delete(s.id)
                .then(() => $scope.savedSearches.removeWhere(search => search.id == s.id));
        };

        $scope.canDeleteSearch = s => {
            return this.agg.$rootScope.user.isCompanyAdmin ||
                this.agg.$rootScope.user.isAdmin ||
                s.createdBy == this.agg.$rootScope.user.id;
        };

        $scope.$watch("binding.selected", (newVal, oldVal) => {
            if (newVal != oldVal) {
                $scope.binding.search = $scope.binding.selected;
                //filters need to be re-filled because they are missing $key in saved searches
                this.fillFilters();
                $scope.reload();
            }
        });


        if (!bypassInit) {
            this.init();
        }
    }
    protected pageSize = document.body.clientHeight > 1130 ? 42 : 22;
    protected epoch: Date = null;
    private filterSourceInfo: { [source: string]: { valueField: string; labelField: string; } };
    public filledFilterSources: string[] = [];

    /** derived classes must call init.  This gives derives classes a chance to fill in scope, etc before things are processed */
    public init() {
        /**
        * could be called in the following ways:
        *      -no query string:  create a new savedSearch and fill it with defaults
        *      -#search=[base64 encoded json string of the search]
        *      -#searchId=[the id of the saved search] :  call the server with the saved search id
        */
        if (this.agg.$state.params.search) {
            this.$scope.binding.search = JSON.parse(this.agg.base64Service.decode(this.agg.$state.params.search));
            this.loadedFromQueryString(this.$scope.binding.search);
        } else {
            this.$scope.binding.search = this.newSearch();
        }

        this.$scope.availableFilters = this.getFiltersFromMeta(this.$scope.binding.search.modelType);
        this.$scope.availableFilters.sortBy(f => f.label);

        this.fillFilters();

        this.refreshSavedSearches();
    }

    public client(api: ApiService): CrudClient<T> {
        throw new Error("client() is abstract");
    }

    /**can be overridden to customize the actual call to search api */
    public searchCall(args: ISearchArgs, tracker: IPromiseTracker): ng.IPromise<T[]> {
        return this.client(this.agg.api).search(args, tracker);
    }

    /**can be overriden to catch when we have just loaded a search via the query string */
    public loadedFromQueryString(search: ISavedSearch) {
        
    }

    /**can be overriden by derived class to provide extra query string params */
    public getQueryStringParams(): {}{
        return {};
    }
    /**
     * the properties of the queried object that should be returned.
     */
    public fields(): string[] {
        return this.getFieldsFromMeta(this.$scope.binding.search.modelType);
    }

    /**
     * a new fresh search.  Can be overriden by the derived controller
     */
    public newSearch(): ISavedSearch {
        return {
            filters: [],
            roles: []
        };
    }

    /** can be overridden by derived class to massage the results before showing them on the screen */
    public massageResults(items: T[]) {
        
    }

    /**
     * can be overridden by derived controllers to add a raw filter to the search
     */
    public getExtraFilters(): IFilter[] {
        return [];
    }

    /**
     * can be overriden by derived controllers to give an extra flag about what we're
     * searching (like configured products needs to be set to "products" so that the 
     * search repo knows what we're doing)
     */
    public getNestedPath(): string {
        return null;
    }

    /**
     * can be overriden by derived controllers to give relative paths to the properties 
     * of the object described in the nestedPath
     */
    public nestedFields(): string[] {
        return [];
    }

    public postProcessFilters(filters: IFilter[]) {
        filters.forEach(f => {
            if (f.type == eFilterType.attribute) {
                let productsScope = this.$scope as any as IProductsScope;
                if (productsScope.attributeDb) {
                    let att = productsScope.attributeDb[f.attributeId];
                    if (att) {
                        if (att.type == eAttributeType.number && att.useRangeBuckets) {
                            if (f.values && f.values.length && f.values[0] != null) {                                
                                f.min = f.values[0]["from"];
                                f.max = f.values[0]["to"];
                            }
                        }
                    }
                }
            }
        });
    }

    protected getSortInfo(): {sortField: string, descending: boolean} {
        let ret = { sortField: this.$scope.binding.search.sortField, descending: this.$scope.binding.search.descending };
        
        //calculate sort
        let s = this.$scope.binding.search;
        if (s.enabled) {
            let sortFilter = s.filters.find(f => f.type == eFilterType.sort);
            if (sortFilter && sortFilter.values != null && sortFilter.values.length > 0 && sortFilter.values[0]) {
                if (sortFilter.sourceType == eFilterSource.productSorts) {
                    let v = sortFilter.values[0] as string;
                    if (v.equalsAny(eProductSortBy.relevance, eProductSortBy.popularity)) {
                        ret.sortField = "score";
                        ret.descending = true;
                    } else if (v == eProductSortBy.priceLowToHigh) {
                        ret.sortField = "price";
                        ret.descending = false;
                    } else if (v == eProductSortBy.priceHighToLow) {
                        ret.sortField = "price";
                        ret.descending = true;
                    } else if (v == eProductSortBy.nameAtoZ) {
                        ret.sortField = "name";
                        ret.descending = false;
                    }
                }
            }
        }

        return ret;
    }

    public getFieldsFromMeta(modelType: string): string[] {
        let m = meta[modelType];
        let fields: string[] = [];
        for (let prop in m) {
            if (prop[0] != "$") {
                let info: { name: string; type: string; control: string; source: string; default: boolean; } = m[prop];
                if (info.type.endsWith("[]")) {
                    // we look at the array type and get it's meta props
                    let subFields = this.getFieldsFromMeta(info.type.substr(0, info.type.length - 2));
                    subFields.forEach(sf => fields.push(info.name + "." + sf));
                } else {
                    fields.push(info.name);
                }
            }
        }
        return fields;
    }

    public getFiltersFromMeta(modelType: string, onlyDefaults?: boolean): IFilter[] {
        let m = meta[modelType];
        let filters: IFilter[] = [];
        for (let prop in m) {
            if (prop[0] != "$") {
                let info: IMetaProperty = m[prop];
                if (!info.permissions ||
                    !info.permissions.length ||
                    info.permissions.every(p => this.agg.$rootScope.user["can" + p.toPascalCase()])
                ) {// check if the current user has permissions to see this filter
                    if (!info.type.endsWith("[]") &&
                        info.control != eFilterControl.none &&
                        (info.default || !onlyDefaults)
                    ) {
                        filters.push({
                            label: loc["generic_" + info.name.toLowerCase() + "_title"] ||
                                loc[m.$name.toLowerCase() + "_" + info.name.toLowerCase() + "_title"] ||
                                info.name.toPascalCase(),
                            type: eFilterType.property,
                            property: info.name,
                            sourceType: info.source,
                            enumType: info.enumType,
                            control: info.control,
                            valueField: this.filterSourceInfo[info.source].valueField,
                            labelField: this.filterSourceInfo[info.source].labelField,
                            headerField: this.filterSourceInfo[info.source].labelField,
                            values: (info.control == eFilterControl.multiSelect? []: [null]),
                            sticky: info.sticky
                        });
                    }
                }
            }
        }
        return filters;
    }

    protected getFilterKey(f: IFilter): string {
        let key = f.sourceType;
        if (f.attributeId) {
            let productsScope = this.$scope as any as IProductsScope;
            key = f.sourceType + "_" + f.attributeId + "_" + productsScope.binding.selectedCategory.id;
        } else if (f.sourceType == eFilterSource.enum){
            key = f.sourceType + "_" + f.enumType;
        }
        return key;
    }

    public fillFilters() { // fills the search filter sources if they haven't already been filled
        this.$scope.binding.search.filters.forEach(f => {
            let key = this.getFilterKey(f);
            f["$key"] = key;
            if (!this.filledFilterSources.some(s => s.isEqual(key))) {
                this.filledFilterSources.push(key);                
                if (f.sourceType == eFilterSource.enum){
                    this.$scope.filterSource[key] = ((this.agg.$filter as any)("enum") as IEnumFilter)(f.enumType, false);
                    if (f.enumType == "eJobStatus"){ // job status is stored as an int in the database
                        let i = 0;
                        for(let v of this.$scope.filterSource[key]){
                            v.value = i;
                            i++;
                        }
                    }
                } else if (f.sourceType == eFilterSource.users) {
                    this.agg.api.users.search({
                        fields: ["id", "firstName", "lastName", "name"], sortField: "firstName"
                    }).then(users => this.$scope.filterSource[key] = users);
                } else if (f.sourceType == eFilterSource.states) {
                    this.agg.api.workflows.states()
                        .then(r => this.$scope.filterSource[key] = [
                            {value: null, label: loc.unsubmitted}, 
                            ...r
                        ]);
                } else if (f.sourceType == eFilterSource.properties) {
                    this.$scope.filterSource[key] = Object.keys(meta[this.$scope.binding.search.modelType + "Meta"]);
                } else if (f.sourceType == eFilterSource.customers) {
                    this.$scope.filterSource[key] = this.agg.api.customers
                        .search({ fields: ["id", "name"] })
                        .then(customers => this.$scope.filterSource[key] = [
                            {id: null, name: loc.none.capitalize()}, 
                            ...customers
                        ]);
                } else if (f.sourceType == eFilterSource.configurators) {
                    // only get the products this user has the rights to see
                    this.$scope.filterSource[key] = this.agg.api.products
                        .searchByCategory({
                            sortField: "name",
                            fields: ["id", "name"],
                            descending: false,
                            filters: [ProductClient.getIsConfiguredFilter(true)],
                            take: 500
                        })
                        .then(configs => this.$scope.filterSource[key] = [
                            {
                                id: null,
                                name: loc.any
                            },
                            ...configs]
                        );
                } else if (f.sourceType == eFilterSource.roles) {
                    this.$scope.filterSource[key] = this.agg.api.roles
                        .search({ fields: ["id", "role"] })
                        .then(roles => this.$scope.filterSource[key] = roles);
                } else if (f.sourceType == eFilterSource.manufacturers) {
                    this.$scope.filterSource[key] = this.agg.api.products.uniqueValues({ property: meta.Product.manufacturer.name })
                        .then(manufacturers => this.$scope.filterSource[key] = manufacturers);
                } else if (f.sourceType == eFilterSource.suggestQuotes) {
                    this.$scope.filterSource[key] = q => this.agg.api.quotes.suggest({ query: q, fields: ["name"] });
                } else if (f.sourceType == eFilterSource.suggestContacts) {
                    this.$scope.filterSource[key] = q => this.agg.api.contacts.suggest({ query: q, fields: ["name"]  });
                } else if (f.sourceType == eFilterSource.suggestCustomers) {
                    this.$scope.filterSource[key] = q => this.agg.api.customers.suggest({ query: q, fields: ["name"]  });
                } else if (f.sourceType == eFilterSource.suggestProducts) {
                    this.$scope.filterSource[key] = q => this.agg.api.products.suggest({ query: q, fields: ["name"] });
                }else if (f.sourceType == eFilterSource.productSorts) {
                    this.$scope.filterSource[key] = [
                        {
                            value: eProductSortBy.relevance,
                            label: loc.eproductsortby_relevance_title
                        }, {
                            value: eProductSortBy.priceLowToHigh,
                            label: loc.eproductsortby_pricelowtohigh_title
                        }, {
                            value: eProductSortBy.priceHighToLow,
                            label: loc.eproductsortby_pricehightolow_title
                        }, {
                            value: eProductSortBy.popularity,
                            label: loc.eproductsortby_popularity_title
                        }, {
                            value: eProductSortBy.nameAtoZ,
                            label: loc.eproductsortby_nameatoz_title
                        }
                    ];
                } else if (f.sourceType == eFilterSource.attributes) {
                    let productsScope = this.$scope as any as IProductsScope;
                    let categoryIds = [];
                    if (productsScope.binding.selectedCategory.id >= 0) {
                        categoryIds = productsScope.getChildCategories(productsScope.binding.selectedCategory);
                    }
                    this.$scope.filterSource[key] = [];
                    if (productsScope.attributeDb) {
                        let att = productsScope.attributeDb[f.attributeId];
                        if (att.type == eAttributeType.number && att.useRangeBuckets) {
                            this.agg.api.products.attributeRanges({ id: att.id,  ranges: att.ranges, categoryIds })
                                .then(buckets => { 
                                    buckets.removeWhere(b => b.count <= 0);
                                    let options: any[] = [];
                                    buckets.forEach(b => {
                                        let label = '';
                                        if (b.from == null) label += "< ";
                                        if (b.to == null) label += "> ";
                                        if (b.from != null) label += b.from.toString();
                                        if (b.from != null && b.to != null) label += " - ";
                                        if (b.to != null) label += b.to.toString();
                                        options.push({ value: b, label: label });
                                    });
                                    this.$scope.filterSource[key].push(...options);
                                });
                        } else {
                            this.agg.api.products.uniqueAttributeValues({ id: att.id, type: att.type, categoryIds })
                                .then(vals => {
                                    this.$scope.filterSource[key].push(...vals);
                                    //if we are confined to a list, merge with the category attribute options so we can get the images
                                    if (att.confineToList) {
                                        for (let v of vals) {
                                            let o = att.options.find(n => n.value == v.value);
                                            if (o) {
                                                v["image"] = o.image;
                                            }
                                        }
                                    }
                                });
                            // if (att.confineToList) {
                            //     this.$scope.filterSource[key].push(...att.options);
                            // } else {
                            //     this.agg.api.products.uniqueAttributeValues({ id: att.id, type: att.type })
                            //         .then(vals => this.$scope.filterSource[key].push(...vals));
                            // }
                        }
                    }
                } else if (f.sourceType == eFilterSource.headerFields) {
                    this.agg.api.quoteHeaders.searchFields().then(r => {
                        this.$scope.filterSource[key] = r
                    });
                }
            }
        });
    }

    public uiOnParamsChanged(changedParams, $transition$) {
        if ($transition$.options().source == "url"){
            if (changedParams.search) {
                this.$scope.binding.search = JSON.parse(this.agg.base64Service.decode(changedParams.search));
            } else {
                this.$scope.binding.search = this.newSearch();
            }
            this.fillSorts(this.$scope.binding.search.sortField, this.$scope.binding.search.descending);
        } 
        this.$scope.query(false);       
    }

    /** can be overriden to handle what happens after items are deleted */
    protected onItemsDeleted(items: T[]) {
        
    }

    protected deleteItems() {
        let selected = this.$scope.results.filter(i => (i as any).$selected);

        if (selected.length > 0) {
            let requiredPromise = this.agg.$q.defer<boolean>();
            
            if (this.$scope.modelType) {
                let ids = selected.map(m => m["id"] as number);
                requiredPromise.promise = this.agg.api.dependencies.isRequiredByDependency(this.$scope.modelType, ids, this.agg.$rootScope.contentTracker);
            }

            requiredPromise.promise.then(required => {
                this.agg.dialogService.dialog({
                    content: required ? loc.msg_deleterequiredbydependency : loc.msg_confirmdelete,
                    buttons: [
                        new DialogButton(loc.ok, icons.success, args => {
                            let tracker = this.agg.$rootScope.contentTracker;
                            let promises: ng.IPromise<any>[] = [];

                            selected.forEach(item => {
                                let promise = this.client(this.agg.api).delete((item as any).id, null, true);
                                tracker.addPromise(promise);
                                promises.push(promise);
                            });

                            this.agg.$q.all(promises).then(() => {
                                this.onItemsDeleted(selected);
                            }).finally(() => {
                                this.$scope.reload(true);
                            });
                        }),
                        new DialogButton(loc.cancel, icons.cancel, args => { })
                    ]
                });
            });

            if (!this.$scope.modelType) {
                requiredPromise.resolve(false);
            }
        }
    }

    protected clone() {
        let selected = this.$scope.results.filter(i => (i as any).$selected);
        let promises: ng.IPromise<any>[] = [];
        selected.forEach(item => {
            let promise = this.client(this.agg.api).clone((item as any).id);
            this.agg.$rootScope.contentTracker.addPromise(promise);
            promises.push(promise);
        });

        this.agg.$q.all(promises).then(() => this.$scope.reload(true));
    }

    protected refreshSavedSearches() {
        if (this.$scope.savedSearchEnabled) {
            return this.agg.api.savedSearches.mySearches(this.$scope.binding.search.modelType).then(r => this.$scope.savedSearches = r);
        }
    }

    protected saveSearchDialog(search: ISavedSearch) {
        search = search || this.$scope.binding.search;
        let dscope: ISaveSearchDialogScope = this.agg.$rootScope.$new();
        if (this.agg.$rootScope.user.isCompanyAdmin || this.agg.$rootScope.user.isAdmin) {
            this.agg.api.roles.search({ take: 1000, sortField: "role" }).then(r => dscope.roles = r);
        }
        dscope.model = { name: "New Search", roles: search.roles || [] };

        this.agg.dialogService.dialog({
            template: Dirs.view("dialog-save-search"),
            scope: dscope,
            buttons: [
                new DialogButton(loc.ok, icons.success, args => {
                    // validate there is a name
                    if (!dscope.model.name) {
                        dscope.validation.name = "Name is required";
                        args.close = false;
                        return;
                    }
                    let newSearch = Object.assign({}, search); // create a new search
                    newSearch.id = null; // clear out the id so it's treated as an insert
                    newSearch.name = dscope.model.name;
                    newSearch.roles = dscope.model.roles;
                    
                    this.agg.api.savedSearches.insert(newSearch, this.agg.$rootScope.contentTracker).then(r => {
                        this.refreshSavedSearches().then(() => {
                            this.$scope.binding.selected = this.$scope.savedSearches.find(ss => ss.id == r.id);
                        });
                    });
                }),
                new DialogButton(loc.close, icons.cancel, args => { })
            ]
        });
    }

    protected fillSorts(sortColumnName: string, descending: boolean){
        for(let col of this.$scope.columns){
            if (!sortColumnName || col.field != sortColumnName) {
                col.sorted = false;
                col.descending = false;
            } else {
                col.sorted = true;
                col.descending = descending;
            }
        }
    }
}
